import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/constants";
import {
  ACTION_STATUSES, KANBAN_COLUMNS, ACTION_STATUS_STYLES, RESPONSIBLE_PARTIES, PARTY_STYLES,
  PRIORITIES, PRIORITY_STYLES,
} from "@/lib/reconConstants";
import { ListChecks, LayoutGrid, Table2, AlertTriangle, Plus, Trash2, ArrowRight } from "lucide-react";
import { RecordActionsMenu, RecordBadges } from "@/components/RecordActions";

export default function TaskBoard() {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [me, setMe] = useState(null);
  const [view, setView] = useState("kanban");
  const [filters, setFilters] = useState({ owner: "all", priority: "all", status: "all", due: "all", q: "" });
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filters.owner !== "all") params.owner = filters.owner;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.due === "overdue") params.due = "overdue";
    api.get("/recon/actions", { params }).then((r) => setActions(r.data)).catch(() => {});
  }, [filters]);
  useEffect(() => { api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const caps = me?.caps || {};
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (a) => a.due_date && a.due_date < today && !["Closed", "Rejected", "Deferred"].includes(a.status);
  const visible = actions.filter((a) => !filters.q || `${a.title} ${a.control_id} ${a.customer_name}`.toLowerCase().includes(filters.q.toLowerCase()));

  const move = async (a, status) => {
    if (!caps.assign || a.status === status) return;
    try { await api.put(`/recon/actions/${a.id}`, { status }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
              <ListChecks className="h-6 w-6 text-gold" aria-hidden="true" /> Reconciliation Tasks
            </h1>
            <p className="text-slate-300 text-sm mt-2">Remediation actions across all reconciliations, by owner, status, and due date.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="view-kanban" onClick={() => setView("kanban")} className={`border-white/20 ${view === "kanban" ? "bg-white/15 text-gold" : "bg-white/5 text-white"}`}><LayoutGrid className="h-4 w-4 mr-1.5" /> Kanban</Button>
            <Button variant="outline" data-testid="view-table" onClick={() => setView("table")} className={`border-white/20 ${view === "table" ? "bg-white/15 text-gold" : "bg-white/5 text-white"}`}><Table2 className="h-4 w-4 mr-1.5" /> Table</Button>
          </div>
        </div>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="rounded-md border border-border bg-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px] space-y-1.5"><Label className="text-xs">Search</Label>
            <Input data-testid="task-search" placeholder="Title, control, customer" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} /></div>
          <Filter label="Owner" testid="filter-owner" value={filters.owner} onChange={(v) => setFilters((f) => ({ ...f, owner: v }))} opts={[["all", "All owners"], ...RESPONSIBLE_PARTIES.map((p) => [p, p])]} />
          <Filter label="Priority" testid="filter-priority" value={filters.priority} onChange={(v) => setFilters((f) => ({ ...f, priority: v }))} opts={[["all", "All"], ...PRIORITIES.map((p) => [p, p])]} />
          <Filter label="Status" testid="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} opts={[["all", "All statuses"], ...ACTION_STATUSES.map((s) => [s, s])]} />
          <Filter label="Due" testid="filter-due" value={filters.due} onChange={(v) => setFilters((f) => ({ ...f, due: v }))} opts={[["all", "All"], ["overdue", "Overdue only"]]} />
        </div>

        {visible.length === 0 ? (
          <div className="rounded-md border border-border bg-card p-12 text-center text-muted-foreground" data-testid="tasks-empty">No action items yet. Create actions from control gaps in a reconciliation workspace.</div>
        ) : view === "kanban" ? (
          <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-2" data-testid="kanban-board">
            {KANBAN_COLUMNS.map((col) => {
              const items = visible.filter((a) => a.status === col);
              return (
                <div key={col} className="rounded-md border border-border bg-slate-50/60 flex flex-col" data-testid={`kanban-col-${col}`}
                  onDragOver={(e) => { if (caps.assign) e.preventDefault(); }}
                  onDrop={() => { if (drag) move(drag, col); setDrag(null); }}>
                  <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                    <span className="text-xs font-semibold text-navy">{col}</span>
                    <span className="text-[11px] text-muted-foreground">{items.length}</span>
                  </div>
                  <div className="p-2 space-y-2 min-h-[80px]">
                    {items.map((a) => (
                      <div key={a.id} data-testid={`task-card-${a.id}`} draggable={caps.assign} onDragStart={() => setDrag(a)} onClick={() => setSelected(a)}
                        className="rounded border border-border bg-white p-2.5 cursor-pointer hover:shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-xs font-medium text-navy line-clamp-2">{a.title}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${PRIORITY_STYLES[a.priority]}`}>{a.priority}</span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${PARTY_STYLES[a.responsible_org]}`}>{a.responsible_org}</span>
                          {a.control_id && <span className="text-[10px] font-mono text-muted-foreground">{a.control_id}</span>}
                        </div>
                        <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                          {a.customer_name}
                          {isOverdue(a) && <span className="text-red-600 flex items-center gap-0.5 ml-auto"><AlertTriangle className="h-3 w-3" /> {a.due_date}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-border text-left">
                {["Action", "Customer", "Control", "Owner", "Priority", "Status", "Due", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}
              </tr></thead>
              <tbody>{visible.map((a) => (
                <tr key={a.id} data-testid={`task-row-${a.id}`} onClick={() => setSelected(a)} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-3 font-medium text-navy max-w-[260px]"><div className="flex items-center gap-1.5"><span className="line-clamp-1">{a.title}</span><RecordBadges classification={a.classification} lifecycleState={a.lifecycle_state} /></div></td>
                  <td className="px-3 py-3 text-xs">{a.customer_name}</td>
                  <td className="px-3 py-3 font-mono text-xs">{a.control_id || "—"}</td>
                  <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded ${PARTY_STYLES[a.responsible_org]}`}>{a.responsible_org}</span></td>
                  <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded border ${PRIORITY_STYLES[a.priority]}`}>{a.priority}</span></td>
                  <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded border ${ACTION_STATUS_STYLES[a.status]}`}>{a.status}</span></td>
                  <td className={`px-3 py-3 text-xs whitespace-nowrap ${isOverdue(a) ? "text-red-600 font-medium" : "text-muted-foreground"}`}>{a.due_date || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}><div className="flex items-center gap-1"><ArrowRight className="h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => setSelected(a)} /><RecordActionsMenu type="action" id={a.id} onChanged={load} /></div></td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        )}
      </div>

      <ActionDialog action={selected} onClose={() => setSelected(null)} caps={caps} onSaved={load} onOpenRecon={(rid) => navigate(`/operations/reconciliation/${rid}`)} />
    </div>
  );
}

function Filter({ label, testid, value, onChange, opts }) {
  return (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[160px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function ActionDialog({ action, onClose, caps, onSaved, onOpenRecon }) {
  const [a, setA] = useState(action);
  const [sub, setSub] = useState({ title: "", responsible_org: "ComplyX" });
  useEffect(() => { setA(action); }, [action]);
  if (!action || !a) return null;
  const patch = async (body) => { try { const r = await api.put(`/recon/actions/${action.id}`, body); setA(r.data); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const addSub = async () => { if (!sub.title.trim()) return; try { const r = await api.post(`/recon/actions/${action.id}/subtasks`, sub); setA(r.data); setSub({ title: "", responsible_org: "ComplyX" }); } catch { toast.error("Failed"); } };

  return (
    <Dialog open={!!action} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{a.title}</DialogTitle>
          <DialogDescription>{a.customer_name}{a.control_id && ` · Control ${a.control_id}`} · from {a.created_from}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {a.description && <p className="text-muted-foreground">{a.description}</p>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner"><Select value={a.responsible_org} onValueChange={(v) => patch({ responsible_org: v })} disabled={!caps.assign}><SelectTrigger data-testid="action-owner"><SelectValue /></SelectTrigger><SelectContent>{RESPONSIBLE_PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Priority"><Select value={a.priority} onValueChange={(v) => patch({ priority: v })} disabled={!caps.assign}><SelectTrigger data-testid="action-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Status"><Select value={a.status} onValueChange={(v) => patch({ status: v })} disabled={!caps.assign}><SelectTrigger data-testid="action-status"><SelectValue /></SelectTrigger><SelectContent>{ACTION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Due date"><Input type="date" data-testid="action-due" defaultValue={a.due_date} disabled={!caps.assign} onBlur={(e) => patch({ due_date: e.target.value })} /></Field>
          </div>
          <Field label="Assigned user"><Input data-testid="action-assigned" defaultValue={a.assigned_user} disabled={!caps.assign} onBlur={(e) => patch({ assigned_user: e.target.value })} placeholder="name@org.com" /></Field>
          <Field label="Required evidence"><Input defaultValue={a.required_evidence} disabled={!caps.assign} onBlur={(e) => patch({ required_evidence: e.target.value })} /></Field>
          <Field label="Internal notes"><Textarea rows={2} defaultValue={a.internal_notes} disabled={!caps.assign} onBlur={(e) => patch({ internal_notes: e.target.value })} /></Field>
          <Field label="Implementer-facing notes"><Textarea rows={2} defaultValue={a.implementer_notes} disabled={!caps.assign} onBlur={(e) => patch({ implementer_notes: e.target.value })} /></Field>
          <Field label="Customer-facing notes"><Textarea rows={2} defaultValue={a.customer_notes} disabled={!caps.assign} onBlur={(e) => patch({ customer_notes: e.target.value })} /></Field>

          {a.responsible_org === "Shared" && (
            <div className="rounded-md border border-border p-3" data-testid="subtasks">
              <div className="text-xs font-semibold text-navy mb-2">Subtasks (Shared responsibility)</div>
              {(a.subtasks || []).map((s) => (
                <div key={s.id} className="flex items-center justify-between text-xs py-1">
                  <span>{s.title} <span className={`px-1.5 py-0.5 rounded ${PARTY_STYLES[s.responsible_org]}`}>{s.responsible_org}</span></span>
                  <span className={`px-1.5 py-0.5 rounded border ${ACTION_STATUS_STYLES[s.status]}`}>{s.status}</span>
                </div>
              ))}
              {caps.assign && (
                <div className="flex gap-2 mt-2">
                  <Input className="h-8 text-xs" placeholder="Subtask title" value={sub.title} onChange={(e) => setSub({ ...sub, title: e.target.value })} data-testid="subtask-title" />
                  <Select value={sub.responsible_org} onValueChange={(v) => setSub({ ...sub, responsible_org: v })}><SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue /></SelectTrigger><SelectContent>{RESPONSIBLE_PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
                  <Button size="sm" className="h-8" data-testid="subtask-add" onClick={addSub}>Add</Button>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenRecon(a.reconciliation_id)} data-testid="action-open-recon">Open Reconciliation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
